import { Link } from 'react-router-dom';
import { trackCtaClick, type MarketingCtaLocation } from '../../lib/marketingEvents';
import type { ComparisonTheme } from './comparisonVisualThemes';
import LogisticsHeroIllustration from './LogisticsHeroIllustration';

const SCHEDULE_DEMO_URL = 'https://meetings.hubspot.com/aditya145';
const APP_SIGNUP = 'https://app.geofleet.ai/signup';
/** Same asset as Navbar / Footer & CompareHubPage */
const GEOFLEET_LOGO_SVG = 'https://cdn.geofleet.ai/images/logo.svg';

export interface ComparisonValueHighlight {
  icon: string;
  title: string;
  sub: string;
}

export interface ComparisonHeroProps {
  competitorName: string;
  h1: string;
  subtext: string;
  pageSlug: string;
  theme: ComparisonTheme;
  location?: MarketingCtaLocation;
  valueHighlights?: readonly ComparisonValueHighlight[];
}

export default function ComparisonHero({
  competitorName,
  h1,
  subtext,
  pageSlug,
  theme,
  location = 'comparison_hero',
  valueHighlights,
}: ComparisonHeroProps) {
  const highlights = valueHighlights ?? [];

  return (
    <section
      className="relative pt-24 pb-12 lg:pt-28 lg:pb-16 overflow-hidden border-b border-slate-100/80"
      style={{
        backgroundColor: '#fafafa',
        backgroundImage: theme.mesh,
      }}
      aria-labelledby="comparison-hero-heading"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.45]"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(${theme.primaryRgb}, 0.06) 0%, transparent 42%, transparent 58%, rgba(${theme.primaryRgb}, 0.05) 100%)`,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage: `radial-gradient(ellipse 70% 45% at 50% 0%, rgba(${theme.primaryRgb}, 0.1), transparent 58%)`,
        }}
        aria-hidden
      />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Flat brand row — no boxes (cf. Mixpanel compare pages) */}
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 md:gap-14 mb-10 lg:mb-12">
          <Link
            to="/"
            className="inline-flex items-center shrink-0 hover:opacity-90 transition-opacity"
            aria-label="Geofleet.ai home"
          >
            <img
              src={GEOFLEET_LOGO_SVG}
              alt="Geofleet.ai"
              className="h-8 sm:h-9 md:h-10 w-auto max-w-[200px] object-contain object-center"
              width={160}
              height={40}
            />
          </Link>
          <span
            className="text-slate-400 text-sm font-semibold tracking-[0.2em] uppercase select-none"
            aria-hidden
          >
            vs
          </span>
          <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight text-center">
            {competitorName}
          </span>
        </div>

        <p className="text-center text-[11px] font-bold uppercase tracking-[0.22em] mb-4 text-slate-500">
          {competitorName} alternative
        </p>

        <h1
          id="comparison-hero-heading"
          className="text-center text-3xl sm:text-4xl lg:text-[2.65rem] font-bold text-slate-900 leading-[1.15] tracking-tight max-w-3xl mx-auto"
        >
          {h1}
        </h1>

        <p className="text-center text-lg sm:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mt-6">
          {subtext}
        </p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mt-10 lg:mt-11">
          <a
            href={SCHEDULE_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold text-white shadow-lg shadow-slate-900/10 transition-opacity hover:opacity-95"
            style={{ backgroundColor: theme.primary }}
            onClick={() =>
              trackCtaClick('Book a Demo', location, { page: pageSlug, competitor: competitorName })
            }
          >
            Book a demo
          </a>
          <a
            href={APP_SIGNUP}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold text-slate-800 bg-white/95 border-2 border-slate-200/90 hover:border-slate-300 hover:bg-white transition-colors shadow-sm"
            onClick={() =>
              trackCtaClick('See Live Dashboard', location, {
                page: pageSlug,
                competitor: competitorName,
              })
            }
          >
            See live dashboard
          </a>
        </div>

        {highlights.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-3 lg:gap-4 mt-12 lg:mt-14 pt-10 border-t border-slate-200/60">
            {highlights.map((pill) => (
              <div
                key={pill.title}
                className="flex gap-3 rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-sm px-4 py-4 text-left shadow-sm"
              >
                <span
                  className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-lg text-white shadow-sm"
                  style={{ backgroundColor: theme.primary }}
                  aria-hidden
                >
                  <i className={pill.icon} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 leading-snug">{pill.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{pill.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 lg:mt-14 max-w-sm mx-auto pt-10 border-t border-slate-200/60">
          <LogisticsHeroIllustration accent={theme.primary} className="max-w-[min(100%,280px)] mx-auto opacity-95" />
          <p className="mt-4 text-center text-xs text-slate-500 leading-snug">
            AI Command Centre: allocation, routing, and alerts in one tower
          </p>
        </div>
      </div>
    </section>
  );
}
